import type { ApiClient } from '@carbonwebmail/api-client'
import { useCallback, useState } from 'react'

/**
 * Naming the organisation a new identity will work in.
 *
 * The only thing this form sends. The address and the name come from the
 * identity provider on the server side, because an address the browser
 * supplied would let anybody claim anybody's — and `users.email` is unique
 * system-wide, so that mistake would also take the real owner's seat.
 *
 * Every refusal is said plainly, which the open signup form this replaces
 * could not do: that one was reachable by anyone, so "already registered"
 * would have made it a way to find out who has an account here. This one needs
 * a verified token and tells its caller only about themselves.
 */

const PROBLEMS = {
  'address-taken':
    'Your address already has an account here. Sign in with it, or ask whoever runs that organisation to invite you.',
  'unverified-address':
    'Confirm your email address first — there should be a message waiting for you.',
} as const

export function useCreateOrganisation({ client }: { readonly client: ApiClient }) {
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [creating, setCreating] = useState(false)

  /**
   * Resolves true when there is an organisation to go to.
   *
   * `already-provisioned` counts: it is what a double-submitted form looks
   * like, and the person does have a place here — sending them to an error
   * screen would be telling them their own account does not exist.
   */
  const create = useCallback(
    async (organizationName: string) => {
      setCreating(true)
      setProblem(undefined)
      try {
        const outcome = await client.signUp({ organizationName })
        if (outcome.status === 'created' || outcome.status === 'already-provisioned') return true

        setProblem(
          outcome.status === 'invalid'
            ? (outcome.problems[0] ?? 'Please check that name and try again.')
            : PROBLEMS[outcome.status],
        )
        return false
      } catch {
        setProblem('We could not set that up just now. Please try again in a moment.')
        return false
      } finally {
        setCreating(false)
      }
    },
    [client],
  )

  return { problem, creating, create }
}
